import axios from 'axios';
import axiosRetry from 'axios-retry';
import cookies from './cookies.js';
import 'abortcontroller-polyfill/dist/abortcontroller-polyfill-only';

const RETRY = 2; // in total 3 times
const DEFAULT_TIMEOUT = 3000; // ms

// Custom error class for better error handling
class HttpError extends Error {
    constructor(message, status, data, contentType) {
        super(message);
        this.name = 'HttpError';
        this.status = status;
        this.data = data;
        this.contentType = contentType;
    }
}

const instance = axios.create({
    // [TODO] comment it out at the moment because the orginal http.js didn't setup timeout.
    // but in the future we may want to enable it back.
    // timeout: DEFAULT_TIMEOUT,
    headers: {'Accept': 'application/json'},
});

/**
Retry condition
// 1. network error -> retry
// - ECONNABORTED (connection timeout)
// - ETIMEDOUT (request timeout)
// - ECONNRESET (connection reset)
// - ENOTFOUND (DNS not found)
//
// 2. 5xx error -> not retry
// - GET, HEAD, OPTIONS, PUT, DELETE
// - POST
//
// 3. specific error -> not retry
// - 429 (Too Many Requests)
// - 500 (Internal Server Error)
// - 502 (Bad Gateway)
// - 503 (Service Unavailable)
// - 504 (Gateway Timeout)
*/
axiosRetry(instance, {
    retries: RETRY,
    retryDelay: axiosRetry.exponentialDelay, // exponential backoff, the interval is getting longger
    retryCondition: (error) => {
        // Don't retry canceled requests
        if (error.code === 'ERR_CANCELED' || error.name === 'CanceledError') {
            return false;
        }
        return axiosRetry.isNetworkError(error);
    },
});

// request interceptor: add CSRF token
instance.interceptors.request.use(
    (config) => {
        const {method, url} = config;

        // Add CSRF token for non-GET/HEAD methods on relative URLs
        if (!['get', 'head'].includes(method.toLowerCase())) {
            if (!/^https?:/.test(url)) {
                config.headers['X-CSRFToken'] = cookies.get('csrftoken');
            }
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    },
);

// response interceptor: Error handling
instance.interceptors.response.use(
    (response) => {
        // IE compatibility: 1223 -> 204
        if (response.status === 1223) {
            response.status = 204;
        }
        return response;
    },
    (error) => {
        // error handling
        if (error?.response) {
            // Server returned an error response
            const {status, data, headers} = error.response;

            // Dispatch custom error event
            const ajaxError = document.createEvent('Event');
            // [TODO:REMOVE] initEvent is deprecated, should remove in the future
            // const ajaxError = new Event('ajaxError', {bubbles: true, cancelable: true});
            ajaxError.initEvent('ajaxError', true, true);
            ajaxError.status = status;
            document.dispatchEvent(ajaxError);

            const httpError = new HttpError(
                `HTTP ${status} error`,
                status,
                data,
                headers['content-type'],
            );
            return Promise.reject(httpError);
        } else if (error.code === 'ECONNABORTED') {
            // timeout
            return Promise.reject(new Error('timeout'));
        } else if (
            error.code === 'ERR_CANCELED' ||
            error.name === 'CanceledError'
        ) {
            // Request was aborted via AbortController
            return Promise.reject(error);
        } else {
            // network error
            return Promise.reject(
                new Error(
                    `Network error: Failed to fetch ${
                        error.config?.url || 'unknown'
                    }`,
                ),
            );
        }
    },
);

/**
 * Convert object to URL-encoded parameters
 * @param {Object} data - Data to parameterize
 * @returns {string} URL-encoded string
 */
function parameterise(data) {
    const params = [];
    for (const k in data) {
        if (!Object.prototype.hasOwnProperty.call(data, k)) {
            continue;
        }
        const v = data[k];
        if (typeof v !== 'undefined' && v !== null) {
            if (Array.isArray(v)) {
                for (const val of v) {
                    params.push(`${k}=${encodeURIComponent(val)}`);
                }
            } else {
                const key = encodeURIComponent(k);
                params.push(`${key}=${encodeURIComponent(v)}`);
            }
        }
    }
    return params.join('&').replace(/%20/g, '+');
}

export default {
    /**
     * Make an HTTP request
     * @param {Object} opts - Request options
     * @param {string} opts.url - Request URL
     * @param {string} [opts.method='GET'] - HTTP method
     * @param {Object|FormData} [opts.data] - Request data
     * @param {Object} [opts.headers] - Custom headers
     * @param {string} [opts.accept] - Accept header value
     * @param {string} [opts.contentType] - Content-Type header value
     * @param {number} [opts.timeout] - Request timeout in ms
     * @param {Function} [opts.handleProgress] - Upload progress handler
     * @returns {Promise} Response data
     */
    async request(opts = {}) {
        if (!opts.url) {
            throw new Error('http.request requires a url parameter.');
        }

        const config = {
            url: opts.url,
            method: opts.method || 'GET',
            headers: opts.headers || {},
            timeout: opts.timeout,
        };

        if (opts.accept) {
            config.headers['Accept'] = opts.accept;
        }

        if (opts.data) {
            if (opts.data instanceof FormData) {
                // FormData: let axios set Content-Type automatically (multipart/form-data)
                config.data = opts.data;
            } else if (opts.contentType) {
                // Custom Content-Type specified
                config.headers['Content-Type'] = opts.contentType;

                // If requesting form-encoded, convert object to string
                if (
                    opts.contentType.includes(
                        'application/x-www-form-urlencoded',
                    )
                ) {
                    config.data =
                        typeof opts.data === 'object'
                            ? parameterise(opts.data)
                            : opts.data;
                } else {
                    config.data = opts.data;
                }
            } else if (typeof opts.data === 'object') {
                if (config.method.toUpperCase() === 'GET') {
                    config.params = opts.data;
                } else {
                    // Default: send as JSON
                    config.data = opts.data;
                    // Axios automatically sets Content-Type: application/json
                }
            } else {
                config.data = opts.data;
            }
        }

        // Handle upload progress
        if (opts?.handleProgress) {
            config.onUploadProgress = opts.handleProgress;
        }

        if (opts?.paramsSerializer) {
            config.paramsSerializer = opts.paramsSerializer;
        }

        // AbortController
        if (opts?.signal) {
            config.signal = opts.signal;
        }

        try {
            const response = await instance.request(config);
            return response.data;
        } catch (error) {
            // We don't log canceled request errors
            if (
                error.code === 'ERR_CANCELED' ||
                error.name === 'CanceledError'
            ) {
                throw error;
            }
            console.error(
                config.url,
                error,
                error?.constructor.name,
                error?.code,
            );
            throw error;
        }
    },

    get(opts = {}) {
        opts.method = 'GET';
        opts.paramsSerializer = {indexes: null};
        return this.request(opts);
    },

    post(opts = {}) {
        opts.method = 'POST';
        return this.request(opts);
    },

    put(opts = {}) {
        opts.method = 'PUT';
        return this.request(opts);
    },

    delete(opts = {}) {
        opts.method = 'DELETE';
        return this.request(opts);
    },

    patch(opts = {}) {
        opts.method = 'PATCH';
        return this.request(opts);
    },

    parameterise,

    // Expose axios instance for advanced usage
    axios: instance,

    // Expose error class for instanceof checks
    HttpError,
};
